/* eslint-disable max-lines, max-lines-per-function, max-statements */
import React from "react";
import {
  Box,
  Chip,
  FormControlLabel,
  Button,
  Link as MuiLink,
  Switch,
  Typography,
} from "@mui/material";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import MaterialTable, { Column } from "@material-table/core";
import {
  mergePropertyContractUpdatedIntoList,
  PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
} from "data/graphql/property-contract-updated-subscription";
import { GET_PLANS_ALL } from "pages/dashboard/admin/plans/queries";
import {
  ContractPdfDownloadUrl,
  ContractPdfDownloadUrlVariables,
  ManagerSettingsPropertyContractsList,
  plansAll,
} from "types/gql-types";
import {
  managerContractDetailHref,
  statusChipColor,
  statusChipLabel,
} from "./contracts-shared";
import { filterManagerContractRows } from "./filter-manager-contract-rows";
import { SettingsHeader } from "./settings-header";

export const CONTRACTS_LIST_QUERY = gql`
  query ManagerSettingsPropertyContractsList {
    propertyContracts {
      id
      propertyName
      planId
      planName
      unitCount
      pricePerUnit
      billingFrequency
      status
      hasPdf
    }
  }
`;

// Presigned URLs expire; fetch fresh at click time so a long-open page still downloads.
export const CONTRACT_PDF_URL_QUERY = gql`
  query ContractPdfDownloadUrl($id: ID!) {
    propertyContractPdfUrl(id: $id)
  }
`;

type Row = ManagerSettingsPropertyContractsList["propertyContracts"][number];
type PlanRow = plansAll["plansAll"][number];
type TableRow = Row & { priceDisplay: string };

const toNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundToCents = (value: number): number => Math.round(value * 100) / 100;

const formatCurrency = (value: number): string => {
  const hasDecimals = !Number.isInteger(value);
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const monthlyLabelFrom = (value: unknown): string => {
  const fallback = toNumberOrNull(value);
  if (fallback === null) return "-";
  return `${formatCurrency(fallback)}/month`;
};

const discountedUnitPrice = (
  monthlyPerUnit: number,
  annualDiscountPercent: number,
  billingFrequency: string,
): number => {
  if (billingFrequency !== "annually" || annualDiscountPercent <= 0) {
    return monthlyPerUnit;
  }
  return roundToCents(monthlyPerUnit * (1 - (annualDiscountPercent / 100)));
};

const priceDisplayFor = (row: Row, plansById: Map<string, PlanRow>): string => {
  const planId = row.planId || "";
  const plan = plansById.get(planId);
  const pricing = plan?.defaultPricing;

  if (!pricing) return monthlyLabelFrom(row.pricePerUnit);

  const baseMonthly = toNumberOrNull(pricing.minimumPrice);
  const threshold = toNumberOrNull(pricing.unitThreshold);
  const monthlyPerUnit = toNumberOrNull(pricing.monthlyPricePerUnit);
  const annualDiscountPercent = toNumberOrNull(pricing.annualDiscountPercent) || 0;
  const units = toNumberOrNull(row.unitCount) || 0;
  const billingFrequency = (row.billingFrequency || "").toLowerCase();
  const hasAnnualDiscount = billingFrequency === "annually" && annualDiscountPercent > 0;

  const thresholdExceeded = threshold !== null && monthlyPerUnit !== null && units > threshold;

  if (thresholdExceeded && monthlyPerUnit !== null) {
    const effectiveUnitPrice = discountedUnitPrice(
      monthlyPerUnit,
      annualDiscountPercent,
      billingFrequency,
    );
    const discountLabel = hasAnnualDiscount ? " (annual discount)" : "";
    return `${formatCurrency(effectiveUnitPrice)}/unit/month${discountLabel}`;
  }

  if (baseMonthly !== null) return `${formatCurrency(baseMonthly)}/month`;

  return monthlyLabelFrom(row.pricePerUnit);
};

const DownloadPdfButton: React.FC<{ contractId: string }> = ({ contractId }) => {
  const [fetchUrl, { loading }] = useLazyQuery<
  ContractPdfDownloadUrl,
  ContractPdfDownloadUrlVariables
  >(CONTRACT_PDF_URL_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      const url = result?.propertyContractPdfUrl;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    },
  });

  const onClick = () => {
    fetchUrl({ variables: { id: contractId } });
  };

  return (
    <Button size="small" variant="outlined" disabled={loading} onClick={onClick}>
      Download
    </Button>
  );
};

const renderPdfCell = (rowData: TableRow) => {
  if (rowData.hasPdf) return <DownloadPdfButton contractId={rowData.id} />;
  return <Typography variant="body2" color="text.secondary">—</Typography>;
};

const columns: Array<Column<TableRow>> = [
  {
    title: "Property",
    field: "propertyName",
    render: (rowData) => (
      <MuiLink href={managerContractDetailHref(rowData.id, true)} underline="hover">
        {rowData.propertyName || "—"}
      </MuiLink>
    ),
  },
  {
    title: "Plan Name",
    field: "planName",
    emptyValue: "—",
  },
  {
    title: "Status",
    field: "status",
    render: (rowData) => (
      <Chip
        size="small"
        variant="outlined"
        label={statusChipLabel(rowData.status)}
        color={statusChipColor(rowData.status)}
      />
    ),
  },
  {
    title: "Pricing",
    field: "priceDisplay",
    sorting: false,
  },
  {
    title: "Units",
    field: "unitCount",
    emptyValue: "—",
  },
  {
    title: "Actions",
    field: "hasPdf",
    sorting: false,
    render: (rowData) => renderPdfCell(rowData),
  },
];

const ContractsIndexPage: React.FC = () => {
  const [hideDraftContracts, setHideDraftContracts] = React.useState(false);
  const { data, loading, error, subscribeToMore } = useQuery<ManagerSettingsPropertyContractsList>(
    CONTRACTS_LIST_QUERY,
  );

  React.useEffect(() => {
    if (!data?.propertyContracts) {
      return () => {};
    }
    const unsubscribe = subscribeToMore({
      document: PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) =>
        mergePropertyContractUpdatedIntoList(prev, subscriptionData),
    });
    return () => {
      unsubscribe();
    };
  }, [data?.propertyContracts, subscribeToMore]);

  const { data: plansData } = useQuery<plansAll>(GET_PLANS_ALL, { fetchPolicy: "cache-first" });
  const contracts: Row[] = data?.propertyContracts || [];
  const plansById = React.useMemo(() => {
    const map = new Map<string, PlanRow>();
    (plansData?.plansAll || []).forEach((plan) => {
      map.set(plan.id, plan);
    });
    return map;
  }, [plansData?.plansAll]);

  const tableRows: TableRow[] = contracts.map((contract) => ({
    ...contract,
    priceDisplay: priceDisplayFor(contract, plansById),
  }));

  const filteredRows = React.useMemo(
    () => filterManagerContractRows(tableRows, hideDraftContracts),
    [tableRows, hideDraftContracts],
  );

  return (
    <div>
      <SettingsHeader title="Contracts">
        <Box sx={{ ml: "auto" }}>
          <FormControlLabel
            control={(
              <Switch
                checked={hideDraftContracts}
                onChange={(_, value) => setHideDraftContracts(value)}
                color="secondary"
              />
            )}
            label="Hide unsigned contracts"
          />
        </Box>
      </SettingsHeader>
      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error.message}
        </Typography>
      )}
      <MaterialTable<TableRow>
        title=""
        columns={columns}
        data={filteredRows}
        isLoading={loading}
        components={{
          Container: function Container (containerProps) {
            return <div {...containerProps} />;
          },
        }}
        options={{
          pageSize: 25,
          pageSizeOptions: [10, 25, 50, 100],
          sorting: true,
          padding: "dense",
          search: true,
          showTitle: false,
        }}
      />
    </div>
  );
};

export default ContractsIndexPage;
